import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { appDate, appMonthName } from "../../../utils/appDateFormat";

/**
 * Locale-correct rendering of the numbers and dates the SERVER computed.
 *
 * This module formats. It never calculates. There is no addition, no
 * multiplication, no proration and no tax arithmetic anywhere in the billing
 * portal — every figure arrives on a quote from `/api/Billing/*` and is passed
 * straight through here. The single client-side computation in the whole
 * feature is a HEADCOUNT subtraction in `ChangePlanDialog` (billable seats minus
 * the target plan's seat cap), which is not money.
 *
 * TWO LOCALE DECISIONS, both deliberate on a finance surface:
 *
 *  - `ar` is requested as `ar-u-nu-latn`, so Arabic keeps its own currency
 *    placement and separators but the DIGITS stay Latin. An Arabic-locale HR
 *    lead reconciles this screen against a PDF invoice, a bank statement and a
 *    card statement, none of which use Arabic-Indic digits. Rendering ١٬٢٣٤ here
 *    and 1,234 on the invoice makes the two impossible to compare at a glance.
 *  - `ar` also pins `ca-gregory`. Some runtimes resolve an Arabic locale to the
 *    Islamic calendar, and a renewal date that reads as a Hijri date next to a
 *    Gregorian invoice period is a support call.
 */

const LOCALE_OVERRIDES: Record<string, string> = {
  ar: "ar-u-nu-latn-ca-gregory",
};

function resolveLocale(language: string | undefined): string {
  const base = (language || "en").split("-")[0].toLowerCase();
  return LOCALE_OVERRIDES[base] || base;
}

/** The UTC calendar day of an instant, as a local-midnight Date the app formatters can read. */
const utcCalendarDay = (date: Date): Date =>
  new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

export function formatMoney(
  amount: number | null | undefined,
  currency: string | null | undefined,
  language: string | undefined,
): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  const locale = resolveLocale(language);
  const code = (currency || "AED").toUpperCase();
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${code} ${amount.toFixed(2)}`;
  }
}

export function formatNumber(
  value: number | null | undefined,
  language: string | undefined,
): string {
  if (value == null || !Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat(resolveLocale(language)).format(value);
  } catch {
    return String(value);
  }
}

export function formatPercent(
  value: number | null | undefined,
  language: string | undefined,
  fractionDigits = 1,
): string {
  if (value == null || !Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat(resolveLocale(language), {
      style: "percent",
      minimumFractionDigits: 0,
      maximumFractionDigits: fractionDigits,
    }).format(value / 100);
  } catch {
    return `${value}%`;
  }
}

export function formatDate(
  iso: string | null | undefined,
  language: string | undefined,
): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  // dd-mm-yyyy (system format), on the UTC calendar day exactly as the previous timeZone: "UTC" render.
  return appDate(utcCalendarDay(date));
}

export function formatLongDate(
  iso: string | null | undefined,
  language: string | undefined,
): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  // Prose date ("ends on 17 September 2026"): day-first with the month NAME in the app language,
  // never the machine (en Intl rendered "September 17, 2026"). UTC calendar day, as before.
  const day = utcCalendarDay(date);
  return `${day.getDate()} ${appMonthName(day, "long")} ${day.getFullYear()}`;
}

/**
 * True when `iso` is already in the past. Callers use it to say "this date has passed" instead of
 * counting down to it — `daysUntil` floors at 0, so on its own it cannot tell "today" from "long
 * gone", and every lapsed tenant was reading a countdown that had already run out.
 */
export function hasPassed(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return false;
  return target < Date.now();
}

/** Whole days from now until `iso`, floored at 0. Used for "renews in N days". */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const diff = Math.ceil((target - Date.now()) / 86_400_000);
  return diff > 0 ? diff : 0;
}

export interface BillingFormatters {
  money: (amount: number | null | undefined, currency?: string | null) => string;
  number: (value: number | null | undefined) => string;
  percent: (value: number | null | undefined, fractionDigits?: number) => string;
  date: (iso: string | null | undefined) => string;
  longDate: (iso: string | null | undefined) => string;
  isRtl: boolean;
  language: string;
}

/** Formatters bound to the active UI language, plus the RTL flag. */
export function useBillingFormat(defaultCurrency?: string | null): BillingFormatters {
  const { i18n } = useTranslation("billing");
  const language = i18n.language || "en";
  const isRtl = i18n.dir() === "rtl";

  return useMemo(
    () => ({
      money: (amount, currency) => formatMoney(amount, currency ?? defaultCurrency, language),
      number: (value) => formatNumber(value, language),
      percent: (value, fractionDigits) => formatPercent(value, language, fractionDigits),
      date: (iso) => formatDate(iso, language),
      longDate: (iso) => formatLongDate(iso, language),
      isRtl,
      language,
    }),
    [language, isRtl, defaultCurrency],
  );
}
